import { useState } from 'react';
import { Platform } from 'react-native';
import { Logger } from '../utils/logger';
import { saveGeneratedFile } from '../utils/downloadFile';
import { CAYMAN_ACADEMY_LOGO_BASE64 } from '../assets/images/caymanAcademyLogoBase64';

let RNHTMLtoPDF: any;
if (Platform.OS !== 'web') {
  try {
    RNHTMLtoPDF = require('react-native-html-to-pdf').default || require('react-native-html-to-pdf');
  } catch (e) {
    console.error('Failed to load PDF modules:', e);
  }
}

interface ReportCardData {
  selectedStudent: any;
  selectedExamResult: any;
  selectedYear: string;
  filtered: any[];
  totalMarks: number;
  maxMarks: number;
  overall: number;
  overallGrade: string;
  resultStatus: string;
  studentImageUrl?: string;
  /** The exam schedule entries (subject, examDate); used to print the exam date. */
  examSchedule?: { examName?: string; examDate?: string }[];
}

export const useReportCardPDF = () => {
  const [downloading, setDownloading] = useState(false);

  const generatePDF = async (data: ReportCardData) => {
    const {
      selectedStudent,
      selectedExamResult,
      selectedYear,
      filtered,
      totalMarks,
      maxMarks,
      overall,
      overallGrade,
      resultStatus,
      studentImageUrl,
      examSchedule,
    } = data;

    if (!selectedExamResult || !selectedStudent) return false;

    try {
      setDownloading(true);

      const getInitials = (name: string) => {
        if (!name) return "ST";
        const parts = name.split(" ").filter(p => p.trim() !== "");
        if (parts.length >= 2) {
          return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
      };

      // The exam's date (or date range) from its schedule; '-' when the schedule has no match.
      const examDateLabel = (() => {
        const names = [selectedExamResult.examName, selectedExamResult.examTypeName]
          .filter(Boolean)
          .map((n: string) => String(n).trim().toLowerCase());
        const days = (examSchedule ?? [])
          .filter(ex => ex.examDate && names.includes(String(ex.examName ?? '').trim().toLowerCase()))
          .map(ex => /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ex.examDate)))
          .filter((m): m is RegExpExecArray => !!m)
          .map(m => `${m[1]}-${m[2]}-${m[3]}`)
          .sort();
        if (days.length === 0) return '-';
        const fmt = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
        return days[0] === days[days.length - 1] ? fmt(days[0]) : `${fmt(days[0])} - ${fmt(days[days.length - 1])}`;
      })();

      const studentInitials = getInitials(selectedStudent.name);
      const avatarHtml = studentImageUrl
        ? `<img src="${studentImageUrl}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 8px;" />`
        : studentInitials;

      const htmlContent = `
        <html>
          <head>
            <style>
              body { font-family: 'Plus Jakarta Sans', Helvetica, Arial, sans-serif; margin: 0; padding: 40px; color: #0f172a; background: white; }
              * { box-sizing: border-box; font-family: 'Plus Jakarta Sans', Helvetica, Arial, sans-serif; }
              .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #cbd5e1; padding-bottom: 24px; margin-bottom: 32px; }
              .schoolInfo { display: flex; align-items: center; gap: 10px; }
              .logoPlaceholder { width: 50px; height: 50px; background-color: #3B82F6; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 800; color: #FFFFFF; }
              .schoolDetails h1 { font-size: 18px; color: #165553; margin: 0 0 4px 0; }
              .schoolContact { margin: 0; font-size: 10px; color: #475569; line-height: 1.5; }
              .letterhead { display: flex; justify-content: space-between; align-items: center; gap: 16px; border-bottom: 3px solid #165553; padding-bottom: 14px; margin-bottom: 20px; }
              .lhBrand { display: flex; align-items: center; gap: 14px; }
              .lhLogo { height: 64px; width: auto; }
              .lhNameBlock { display: flex; flex-direction: column; }
              .lhName { display: flex; flex-direction: column; font-size: 26px; font-weight: 800; line-height: 0.95; color: #165553; letter-spacing: 0; }
              .lhRule { width: 100%; height: 2px; background-color: #165553; margin: 4px 0 3px 0; }
              .lhTagline { font-size: 7.5px; font-weight: 700; color: #165553; letter-spacing: 0.2px; }
              .lhContact { font-size: 10px; line-height: 1.5; color: #334155; text-align: left; }
              .lhTitle { text-align: right; flex-shrink: 0; }
              .lhTitle h2 { display: inline-block; font-size: 16px; color: #165553; margin: 0 0 6px 0; border-bottom: 3px solid #EEC906; padding-bottom: 4px; }
              .lhTitle p { margin: 0; font-size: 12px; font-weight: 500; color: #334155; }
              .reportTitle { text-align: right; }
              .reportTitle h2 { font-size: 18px; color: #165553; margin: 0 0 4px 0; }
              .reportTitle p { margin: 0; font-size: 13px; font-weight: 500; color: #334155; }
              .studentInfoContainer { display: flex; justify-content: space-between; align-items: stretch; margin-bottom: 32px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 20px 24px; background-color: #f8fafc; }
              .studentInfoGrid { display: grid; grid-template-columns: 1.2fr 1fr; column-gap: 32px; row-gap: 12px; flex: 1; }
              .infoRow { display: flex; font-size: 12px; align-items: flex-start; }
              .infoLabel { width: 120px; font-weight: 700; color: #1e293b; flex-shrink: 0; }
              .infoColon { margin-right: 12px; color: #1e293b; font-weight: 600; }
              .infoValue { color: #475569; font-weight: 500; flex: 1; }
              .studentInitialsAvatar { width: 100px; height: 100px; border-radius: 8px; margin-left: 32px; background-color: #e2e8f0; display: flex; align-items: center; justify-content: center; font-size: 32px; font-weight: bold; color: #475569; flex-shrink: 0; align-self: center; border: 1px solid #e2e8f0; }
              .sectionTitle { background-color: #165553; border-bottom: 3px solid #EEC906; color: white; padding: 6px 16px; font-size: 14px; font-weight: 600; display: inline-block; margin-bottom: 16px; border-radius: 4px; text-transform: uppercase; }
              .marksTable { width: 100%; border-collapse: collapse; margin-bottom: 32px; border: 1px solid #e2e8f0; }
              .marksTable th { background-color: #165553; color: white; font-size: 11px; font-weight: 600; padding: 8px 6px; text-align: center; border: 1px solid rgba(255, 255, 255, 0.2); }
              .marksTable th:first-child { text-align: left; }
              .marksTable td { padding: 6px 4px; text-align: center; font-size: 12px; color: #334155; border: 1px solid #e2e8f0; }
              .marksTable td:first-child { text-align: left; font-weight: 500; color: #0f172a; }
              .summaryCardsRow { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 32px; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; page-break-inside: avoid; }
              .summaryCard { display: flex; flex-direction: column; align-items: center; gap: 12px; flex: 1; position: relative; }
              .summaryCard:not(:last-child)::after { content: ''; position: absolute; right: -8px; top: 10%; height: 80%; width: 1px; background-color: #e2e8f0; }
              .summaryIcon { width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
              .iconGray { background: #f1f5f9; color: #64748b; }
              .iconBlue { background: #e3f0ef; color: #165553; }
              .iconStar { background: #fdf6c8; color: #a16207; }
              .iconGreen { background: #dcfce7; color: #16a34a; }
              .iconGold { background: #fef9c3; color: #eab308; }
              .summaryLabel { font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; }
              .summaryValue { font-size: 14px; font-weight: 600; color: #0f172a; }
              .textGreen { color: #16a34a; font-weight: 600; }
              .graphScaleLayout { display: flex; gap: 32px; margin-bottom: 32px; page-break-inside: avoid; }
              .graphSection { flex: 1 1 0; min-width: 0; }
              .scaleSection { flex: 1 1 0; min-width: 230px; }
              .graphContainer { display: flex; align-items: flex-end; height: 200px; gap: 10px; padding: 0 8px; border-bottom: 1px solid #94a3b8; border-left: 1px solid #94a3b8; position: relative; margin-left: 40px; margin-top: 28px; margin-bottom: 48px; background-image: linear-gradient(to top, #e2e8f0 1px, transparent 1px); background-size: 100% 25%; }
              .yAxis { position: absolute; left: -40px; top: -6px; height: calc(100% + 12px); display: flex; flex-direction: column; justify-content: space-between; font-size: 10px; line-height: 12px; color: #64748b; }
              .barWrapper { flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; position: relative; }
              .barValue { font-size: 9px; white-space: nowrap; font-weight: 600; color: #334155; margin-bottom: 3px; }
              .bar { width: 100%; max-width: 32px; background-color: #165553; border-radius: 4px 4px 0 0; }
              .barLabel { position: absolute; top: 100%; left: 0; right: 0; margin-top: 6px; font-size: 9px; line-height: 1.2; color: #475569; text-align: center; max-height: 2.4em; overflow: hidden; word-break: break-word; }
              .textRed { color: #dc2626; font-weight: 600; }
              .scaleTable { width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; }
              .scaleTable th { background-color: #f8fafc; color: #0f172a; font-size: 12px; font-weight: 600; padding: 8px; border: 1px solid #e2e8f0; }
              .scaleTable td { padding: 8px; text-align: center; font-size: 12px; color: #334155; border: 1px solid #e2e8f0; }
              .remarksContainer { display: flex; gap: 32px; margin-bottom: 64px; page-break-inside: avoid; }
              .remarkBox { flex: 1; border: 1px solid #e2e8f0; border-radius: 8px; padding: 24px; position: relative; }
              .remarkHeader { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; font-weight: 600; color: #0f172a; }
              .remarkIcon { color: #64748b; }
              .remarkText { font-size: 13px; color: #475569; line-height: 1.6; margin-bottom: 40px; }
              .signatureArea { display: flex; flex-direction: column; gap: 8px; }
              .signatureLabel { font-size: 12px; color: #64748b; }
              .signatureName { font-family: 'cursive', 'Brush Script MT', 'Lucida Handwriting', sans-serif; font-size: 20px; color: #165553; margin-top: 8px; min-height: 24px; }
              .page-break { page-break-before: always; margin-top: 20px; }
            </style>
          </head>
          <body>
<div class="letterhead">
              <div class="lhBrand">
                <img src="${CAYMAN_ACADEMY_LOGO_BASE64}" alt="Cayman Academy" class="lhLogo" />
                <div class="lhNameBlock">
                  <div class="lhName"><span>CAYMAN</span><span>ACADEMY</span></div>
                  <div class="lhRule"></div>
                  <div class="lhTagline">A SEVENTH-DAY ADVENTIST INSTITUTION</div>
                </div>
              </div>
              <div class="lhContact">
                <div>211 Walkers Road, George Town,</div>
                <div>Cayman Island. KY1-1106</div>
                <div>Office.secretary@caymanacademy.ky</div>
                <div>www.caymanacademy.ky</div>
                <div>1 (345) 640-2630</div>
              </div>
              <div class="lhTitle">
                <h2>ACADEMIC REPORT CARD</h2>
                <p>Academic Session : <span>${selectedYear}</span></p>
              </div>
            </div>
            <div class="studentInfoContainer">
              <div class="studentInfoGrid">
                <div class="infoRow"><span class="infoLabel">Student Name</span><span class="infoColon">:</span><span class="infoValue">${selectedStudent.name || '-'}</span></div>
                <div class="infoRow"><span class="infoLabel">Admission No.</span><span class="infoColon">:</span><span class="infoValue">${selectedStudent.studentIdentificationNumber || '-'}</span></div>
                <div class="infoRow"><span class="infoLabel">Exam Type</span><span class="infoColon">:</span><span class="infoValue">${selectedExamResult.examTypeName || selectedExamResult.examName || '-'}</span></div>
                <div class="infoRow"><span class="infoLabel">Exam Date</span><span class="infoColon">:</span><span class="infoValue">${examDateLabel}</span></div>
                <div class="infoRow"><span class="infoLabel">Class & Section</span><span class="infoColon">:</span><span class="infoValue">${selectedStudent.class || '-'}${selectedStudent.section ? ' - ' + selectedStudent.section : ''}</span></div>
                <div class="infoRow"><span class="infoLabel">Result Date</span><span class="infoColon">:</span><span class="infoValue">${new Date().toLocaleDateString('en-GB')}</span></div>
              </div>
              <div class="studentInitialsAvatar">${avatarHtml}</div>
            </div>

            <div>
              <div class="sectionTitle">SUBJECT WISE PERFORMANCE</div>
              <table class="marksTable">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Max Marks</th>
                    <th>Obtained Marks</th>
                    <th>Percentage</th>
                    <th>Grade</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  ${filtered.map((subj: any) => {
        let remarks = subj.remarks;
        if (!remarks || remarks === '-') {
          if (subj.grade?.includes('A')) remarks = 'Excellent';
          else if (subj.grade?.includes('B')) remarks = 'Good';
          else if (subj.grade?.includes('C')) remarks = 'Satisfactory';
          else if (subj.grade === 'D' || subj.grade === 'E' || subj.grade === 'F') remarks = 'Needs Improvement';
          else remarks = '-';
        }
        return `
                    <tr>
                      <td>${subj.subjectName}</td>
                      <td>${subj.maxMarks}</td>
                      <td>${subj.obtainedMarks}</td>
                      <td>${typeof subj.percentage === 'number' ? subj.percentage.toFixed(2) : subj.percentage}%</td>
                      <td>${subj.grade || '-'}</td>
                      <td>${remarks}</td>
                    </tr>
                    `;
      }).join('')}
                </tbody>
              </table>
            </div>
            
            <div class="page-break"></div>

            <div>
              <div class="sectionTitle">PERFORMANCE SUMMARY</div>
              <div class="summaryCardsRow">
                <div class="summaryCard">
                  <div class="summaryIcon iconGray">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect><path d="M12 11h4"></path><path d="M12 16h4"></path><path d="M8 11h.01"></path><path d="M8 16h.01"></path></svg>
                  </div>
                  <div class="summaryLabel">Total Marks</div>
                  <div class="summaryValue">${totalMarks} / ${maxMarks}</div>
                </div>
                <div class="summaryCard">
                  <div class="summaryIcon iconBlue">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="5" x2="5" y2="19"></line><circle cx="6.5" cy="6.5" r="2.5"></circle><circle cx="17.5" cy="17.5" r="2.5"></circle></svg>
                  </div>
                  <div class="summaryLabel">Percentage</div>
                  <div class="summaryValue">${typeof overall === 'number' ? overall.toFixed(2) : overall}%</div>
                </div>
                <div class="summaryCard">
                  <div class="summaryIcon iconStar">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                  </div>
                  <div class="summaryLabel">Overall Grade</div>
                  <div class="summaryValue">${overallGrade}</div>
                </div>
                <div class="summaryCard">
                  <div class="summaryIcon iconGreen">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="7"></circle><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline></svg>
                  </div>
                  <div class="summaryLabel">Result</div>
                  <div class="summaryValue ${String(resultStatus).toLowerCase() === 'fail' ? 'textRed' : 'textGreen'}">${resultStatus}</div>
                </div>
                <div class="summaryCard">
                  <div class="summaryIcon iconGold">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path></svg>
                  </div>
                  <div class="summaryLabel">Class Rank</div>
                  <div class="summaryValue">-</div>
                </div>
                <div class="summaryCard">
                  <div class="summaryIcon iconBlue">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  </div>
                  <div class="summaryLabel">Attendance</div>
                  <div class="summaryValue">-</div>
                </div>
              </div>
            </div>

            <div class="graphScaleLayout">
              <div class="graphSection">
                <div class="sectionTitle">SUBJECT WISE GRAPH</div>
                <div class="graphContainer">
                  <div class="yAxis">
                    <span>100%</span><span>75%</span><span>50%</span><span>25%</span><span>0%</span>
                  </div>
                  ${filtered.map((subj: any) => {
        const percentage = Math.round(subj.percentage);
        // Full name; the label wraps (two lines) instead of being cut to 8 characters.
        const shortName = subj.subjectName;
        return `
                    <div class="barWrapper">
                      <div class="barValue">${percentage}%</div>
                      <div class="bar" style="height: ${percentage}%"></div>
                      <div class="barLabel">${shortName}</div>
                    </div>
                    `;
      }).join('')}
                </div>
              </div>
            </div>

            <div style="margin-top: 32px;">
              <div class="sectionTitle">REMARKS</div>
              <div class="remarksContainer">
                <div class="remarkBox">
                  <div class="remarkHeader">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="remarkIcon"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect><path d="M12 11h4"></path><path d="M12 16h4"></path><path d="M8 11h.01"></path><path d="M8 16h.01"></path></svg>
                    Class Teacher Remarks
                  </div>
                  <p class="remarkText">
                    Consistent performance with excellent potential. Keep improving and maintain the same dedication.
                  </p>
                  <div class="signatureArea">
                    <span class="signatureLabel">Class Teacher Signature</span>
                    <div class="signatureName">&nbsp;</div>
                  </div>
                </div>

                <div class="remarkBox">
                  <div class="remarkHeader">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="remarkIcon"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    Principal Remarks
                  </div>
                  <p class="remarkText">
                    Excellent progress. Keep up the good work and continue to strive for excellence in all academic endeavors.
                  </p>
                  <div class="signatureArea">
                    <span class="signatureLabel">Principal Signature</span>
                    <div class="signatureName">&nbsp;</div>
                  </div>
                </div>
              </div>
            </div>
            
          </body>
        </html>
      `;

      const fileName = `${(selectedStudent.name || 'Student').replace(/\s+/g, '_')}_${selectedExamResult.examName.replace(/\s+/g, '_')}`;

      if (Platform.OS === 'web') {
        const html2pdf = require('html2pdf.js');
        const element = (globalThis as any).document.createElement('div');
        element.innerHTML = htmlContent;

        const opt = {
          margin: 10,
          filename: `${fileName}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        return new Promise<boolean>((resolve) => {
          html2pdf().set(opt).from(element).save().then(() => {
            setDownloading(false);
            resolve(true);
          }).catch((err: any) => {
            Logger.error('useReportCardPDF', 'Error generating web PDF:', err);
            setDownloading(false);
            resolve(false);
          });
        });
      } else {
        if (!RNHTMLtoPDF) {
          setDownloading(false);
          return false;
        }

        let options = {
          html: htmlContent,
          fileName: fileName,
          directory: 'Documents',
        };

        let file = await RNHTMLtoPDF.generatePDF(options);

        if (file.filePath) {
          await saveGeneratedFile(file.filePath, `${fileName}.pdf`, 'application/pdf');
          setDownloading(false);
          return true;
        }
        setDownloading(false);
        return false;
      }
    } catch (error) {
      Logger.error('useReportCardPDF', 'Error generating PDF:', error);
      setDownloading(false);
      return false;
    }
  };

  return { generatePDF, downloading };
};
